import {
  generateId,
  getDatabase,
  sanitizeUser,
  updateDatabase
} from '../data/localDataStore';

export const userManagementService = {
  async getAllUsers() {
    try {
      const database = getDatabase();
      const users = database.users.map((user) => sanitizeUser(user));
      return { success: true, users };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nel recuperare gli utenti'
      };
    }
  },

  async updateUser(userId, updates) {
    try {
      let updatedUser = null;

      updateDatabase((draft) => {
        const targetUser = draft.users.find((user) => user.id === userId);
        if (!targetUser) {
          return;
        }

        Object.assign(targetUser, updates, {
          updated_at: new Date().toISOString()
        });

        updatedUser = sanitizeUser(targetUser);
      });

      if (!updatedUser) {
        return {
          success: false,
          error: 'Utente non trovato'
        };
      }

      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nell\'aggiornare l\'utente'
      };
    }
  },

  async deactivateUser(userId) {
    return this.updateUser(userId, { is_active: false });
  },

  async activateUser(userId) {
    return this.updateUser(userId, { is_active: true });
  },

  async createUser(userData) {
    try {
      const database = getDatabase();
      const normalizedUsername = userData?.username?.trim()?.toLowerCase();

      if (!normalizedUsername) {
        return { success: false, error: 'Nome utente non valido' };
      }

      const exists = database.users.some((user) => user.username === normalizedUsername);
      if (exists) {
        return { success: false, error: 'Nome utente già esistente' };
      }

      const newUser = {
        id: generateId('user'),
        username: normalizedUsername,
        password: userData?.password,
        full_name: userData?.fullName?.trim(),
        email: `${normalizedUsername}@sistema-ordini.local`,
        role: userData?.role || 'order_user',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login_at: null
      };

      updateDatabase((draft) => {
        draft.users.push(newUser);
      });

      return { success: true, user: sanitizeUser(newUser) };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nella creazione dell\'utente'
      };
    }
  },

  async editUser(userId, userData) {
    try {
      const database = getDatabase();
      const normalizedUsername = userData?.username?.trim()?.toLowerCase();

      if (!normalizedUsername) {
        return { success: false, error: 'Nome utente non valido' };
      }

      const usernameTaken = database.users.some(
        (user) => user.id !== userId && user.username === normalizedUsername
      );

      if (usernameTaken) {
        return { success: false, error: 'Nome utente già esistente' };
      }

      let updatedUser = null;

      updateDatabase((draft) => {
        const targetUser = draft.users.find((user) => user.id === userId);
        if (!targetUser) {
          return;
        }

        targetUser.username = normalizedUsername;
        targetUser.full_name = userData?.fullName?.trim();
        targetUser.role = userData?.role || targetUser.role;
        targetUser.email = `${normalizedUsername}@sistema-ordini.local`;
        targetUser.updated_at = new Date().toISOString();

        if (userData?.password?.trim()) {
          targetUser.password = userData.password.trim();
        }

        updatedUser = sanitizeUser(targetUser);
      });

      if (!updatedUser) {
        return { success: false, error: 'Utente non trovato' };
      }

      return { success: true, user: updatedUser };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nella modifica dell\'utente'
      };
    }
  },

  async deleteUser(userId) {
    try {
      let deleted = false;

      updateDatabase((draft) => {
        const originalLength = draft.users.length;
        draft.users = draft.users.filter((user) => user.id !== userId);
        deleted = draft.users.length !== originalLength;
      });

      return deleted
        ? { success: true }
        : { success: false, error: 'Utente non trovato' };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nella cancellazione dell\'utente'
      };
    }
  }
};

export default userManagementService;
